/**
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║                    MONITORADOR DE OBJETOS - V2                        ║
 * ║              Sistema Centralizado de Gerenciamento do Monitor         ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 * 
 * Este é o ÚNICO ponto de entrada para gerenciar o monitor do CorelDRAW.
 * 
 * PRINCÍPIOS:
 * - Singleton: Uma única instância compartilhada por toda aplicação
 * - Lifecycle: OrderDetailsWindow controla início/parada do monitor
 * - Thread-Safe: Protege contra chamadas concorrentes (status, sync)
 * - Event-Driven: Sistema de eventos para integração com React
 * 
 * PIPELINES SUPORTADOS:
 * ├── Sales (Salvamento)        → initForSales()
 * ├── Production (Exportação)   → initForProduction()
 * ├── Export (Exportação Batch) → initForExport()
 * └── Generic (Uso geral)       → iniciarMonitoramento()
 * 
 * USO RECOMENDADO:
 * - React: Use o hook useMonitoramentoObjetos()
 * - Services: Use os métodos públicos diretamente
 * - Cleanup: OrderDetailsWindow para o monitor ao desmontar
 */

import { corelCommunication } from './communication';
import { sendToTerminal, sendToTestLog, logger } from '../terminalLogger';
import { SystemService } from './actions/system';
import { MetadataService } from './actions/metadata';
import { OPERATION_DELAYS } from './pipeline/pipelineConstants';
import { MonitorResponseAdapter } from './adapters/monitorResponseAdapter';
import { LogCategory } from '@/config/logging';
import { DEBUG_INGEST_URL } from '@/config';
import type { 
  MonitorStatusData, 
  MonitorSyncData, 
  MonitorIniciarData 
} from './types/monitoring';

// Tipos de eventos possíveis
/**
 * Tipos de eventos disparados pelo monitorador.
 * - 'alterado': atributos do objeto foram modificados
 * - 'removido': objeto foi removido do documento
 * - 'criado': um novo objeto foi criado
 * - 'documento': eventos do documento (aberto, fechado, trocado)
 * - '*': curinga; recebe todos os eventos emitidos
 */
type TipoEvento = 'alterado' | 'removido' | 'criado' | 'documento' | '*';
/**
 * Tipo da mudança detectada em um objeto monitorado.
 */
type TipoMudanca = 'nome' | 'dimensoes' | 'camada' | 'ordem' | 'novo' | 'removido';

// Interface para dados do objeto (formato real do C#)
/**
 * Estrutura bruta do objeto reportada pelo backend C# (PascalCase).
 * Alguns campos são opcionais conforme o tipo de objeto/evento.
 */
interface DadosObjeto {
  Nome: string;
  Largura: number;
  Altura: number;
  PosicaoX?: number;
  PosicaoY?: number;
  Camada: string;
  Tipo?: string;
  Existe?: boolean;
  Ordem: number;
  StaticID: string;
}

// Interface para notificação de mudança (formato real do C#)
/**
 * Payload de notificação de alteração recebido do backend C#.
 * Observação: campos em camelCase (ex.: staticId, pageIndex, bbox) podem ser adicionados
 * localmente via normalização para compatibilidade com hooks existentes.
 */
interface NotificacaoMudanca {
  tipo: 'alterado' | 'removido' | 'criado' | 'documento';
  nome: string;
  largura: number;
  altura: number;
  camada: string;
  existe: boolean;
  tipoMudanca: TipoMudanca;
  timestamp: string;
  dadosAnteriores: DadosObjeto | null;
  dadosAtuais: DadosObjeto | null;
  // Campos novos (camelCase), opcionais e retrocompatíveis
  staticId?: string;
  pageIndex?: number;
  bbox?: { left: number; right: number; top: number; bottom: number } | null;
  zOrder?: number;
  // Campos para eventos de documento
  status?: 'fechado' | 'trocado' | 'aberto';
  arquivoAnterior?: string;
  arquivoAtual?: string;
  // Novos campos para estrutura unificada
  categoria?: 'objeto' | 'documento';
  acao?: 'atualizar' | 'remover' | 'status';
  objeto?: {
    staticId: string;
    nome: string;
    largura: number;
    altura: number;
    camada: string;
    pageIndex: number;
    zOrder: number;
    bbox: { left: number; right: number; top: number; bottom: number };
    meta: {
      guid?: string | null;
      nomeBase?: string | null;
      multiplicador?: number | null;
      contracao?: string | null;
      exportIndex?: number | null;
    };
  };
  mudancas?: string[];
  diff?: { antes: any; depois: any };
}

// Interface para configuração de monitoramento
/**
 * Parâmetros de configuração para otimização do monitoramento
 */
interface ConfiguracaoMonitoramento {
  pollingInterval?: number;      // ms entre verificações (padrão: 200)
  batchSize?: number;           // objetos por mensagem (padrão: 1)
  snapshotPaginado?: boolean;   // carregamento progressivo (padrão: false)
  snapshotPageSize?: number;    // objetos por página (padrão: 50)
  pin?: boolean;                // fixar documento ativo (padrão: false)
}

/**
 * Contexto de execução para adaptar comportamento do monitor
 */
type MonitorContext = 'sales' | 'production' | 'export' | 'generic';

/**
 * Resultado de operações do monitor
 */
interface MonitorResult {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

// Interface para estatísticas
/**
 * Estatísticas agregadas sobre handlers registrados por tipo de evento.
 */
interface Estatisticas {
  handlers: {
    alterado: number;
    removido: number;
    criado: number;
    total: number;
  };
}

// Tipo para handlers de eventos
/**
 * Assinatura de função chamada quando um evento monitorado é recebido.
 */
type EventHandler = (dados: NotificacaoMudanca) => void;

/**
 * Gerencia o monitoramento de objetos do CorelDraw no frontend.
 * 
 * ARQUITETURA:
 * - Singleton compartilhado por toda aplicação
 * - Lifecycle controlado pelo OrderDetailsWindow (inicia na pipeline, para ao fechar)
 * - Mutexes para proteger operações críticas (status, sync)
 * - Sistema de eventos para integração com componentes React
 * 
 * MÉTODOS PRINCIPAIS:
 * ├── Lifecycle
 * │   ├── iniciarMonitoramento()          - Iniciar genérico
 * │   ├── iniciarComConfiguracao()        - Iniciar com configuração
 * │   ├── initForSales()                  - Sales pipeline (verifica/para monitor anterior)
 * │   ├── initForProduction()             - Production pipeline (pipeline já parou monitor)
 * │   ├── initForExport()                 - Export pipeline (apenas sync)
 * │   ├── pararMonitoramento()            - Para monitor
 * │   ├── stopWithRetry()                 - Para com retry
 * │   └── ensureMonitorReady()            - Garantir monitor pronto
 * ├── Status & Sync
 * │   ├── verificarStatus()               - Status atual
 * │   ├── sincronizarObjetos()            - Sync completo
 * │   ├── snapshot()                      - Snapshot do documento
 * │   └── coletarMetadados()              - Coletar metadados de objetos
 * └── Eventos
 *     ├── on()                            - Registrar handler
 *     ├── off()                           - Remover handler
 *     └── getEstatisticas()               - Estatísticas de handlers
 * 
 * ⚠️ IMPORTANTE - Diferença entre init methods:
 * - initForSales(): chamado DIRETAMENTE → verifica/para monitor anterior
 * - initForProduction(): chamado por PIPELINE → pipeline já verificou/parou
 */

export class MonitoradorObjetos {
  private monitoramentoAtivo: boolean = false;
  private handlers: Map<TipoEvento, Set<EventHandler>> = new Map();
  private webViewInicializado: boolean = false;
  private _statusInProgress: boolean = false;
  private _syncInProgress: boolean = false;
  private _stopInProgress: boolean = false;
  private _currentContext: MonitorContext = 'generic';
  private currentDocKey: string | null = null; // docKey do documento sendo monitorado (para filtragem)

  /**
   * Flag que indica se o monitor foi parado para salvamento
   * Quando true, TODOS os eventos são ignorados (incluindo documento fechado)
   */
  private _stoppedForSave: boolean = false;

  /**
   * Obtém o docKey atual do documento monitorado
   * Útil para hooks que precisam do docKey quando o monitor já está ativo
   */
  public getCurrentDocKey(): string | null {
    return this.currentDocKey;
  }

  /**
   * Verifica se o monitor foi parado para salvamento
   * Quando true, handlers NÃO devem processar eventos
   */
  public isStoppedForSave(): boolean {
    return this._stoppedForSave;
  }

  private _monitorStartedAt: number = 0; // Timestamp de quando o monitor foi iniciado (para proteção de race condition)

  constructor() {
    // Registrar handler para eventos via sistema centralizado
    // 🔇 Logs de eventos removidos para reduzir verbosidade (cada objeto gerava 1 log)
    corelCommunication.registerEventHandler('monitorar-objetos', (data) => {
      this.processarNotificacao(data);
    });
  }

  /**
   * Inicializa o WebView2 no backend C# antes de qualquer comando
   */
  private async inicializarWebView(): Promise<boolean> {
    if (this.webViewInicializado) {
      return true;
    }

    try {
      // Verificar se o WebView2 está disponível
      if (typeof window === 'undefined' || !window.chrome?.webview) {
        return false;
      }

      this.webViewInicializado = true;
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Captura docKey da resposta do backend (método privado para evitar duplicação)
   * @param resposta Resposta do backend após iniciar monitoramento
   * @param logMessage Mensagem personalizada para log (opcional)
   */
  private capturarDocKeyDaResposta(resposta: any, logMessage?: string): void {
    if (!MonitorResponseAdapter.isSuccess(resposta)) {
      return;
    }

    try {
      const docKey = resposta?.result?.data?.docKey;
      if (docKey && typeof docKey === 'string') {
        this.currentDocKey = docKey;
        this._monitorStartedAt = Date.now(); // Registrar timestamp para proteção de race condition
        sendToTerminal(
          logMessage || '🔑 [MONITOR] docKey capturado',
          { docKey, monitorStartedAt: this._monitorStartedAt },
          LogCategory.EVENT
        );
      }
    } catch (err) {
      // Silencioso - docKey pode não estar disponível
    }
  }

  /**
   * ✅ NOVO: Registra docKey manualmente (usado pela pipeline unificada)
   * Quando a pipeline unificada do C# inicia o monitor, precisamos registrar
   * o docKey aqui para que a proteção contra eventos antigos funcione corretamente
   * @param docKey DocKey do documento monitorado
   * @param logMessage Mensagem personalizada para log (opcional)
   */
  public registrarDocKey(docKey: string, logMessage?: string): void {
    if (!docKey || typeof docKey !== 'string') {
      return;
    }

    try {
      this.currentDocKey = docKey;
      this._monitorStartedAt = Date.now(); // Registrar timestamp para proteção de race condition
      sendToTerminal(
        logMessage || '🔑 [MONITOR] docKey registrado manualmente',
        { docKey, monitorStartedAt: this._monitorStartedAt },
        LogCategory.EVENT
      );
    } catch (err) {
      // Silencioso - docKey pode não estar disponível
    }
  }

  /**
   * Inicia o monitoramento no backend C# para começar a receber eventos de objetos.
   * @returns Promise que indica se o backend confirmou a ativação do monitoramento.
   */
  public async iniciarMonitoramento(): Promise<boolean> {
    try {
      // Primeiro, garantir que o WebView2 está disponível
      const webViewOk = await this.inicializarWebView();
      if (!webViewOk) {
        return false;
      }
      
      // Enviar action para o backend C# ativar o monitoramento
      const resposta = await this.executarComando({
        action: 'monitorar-objetos',
        data: { comando: 'iniciar' }
      });

      if (MonitorResponseAdapter.isSuccess(resposta)) {
        this.monitoramentoAtivo = true;
        
        // Capturar docKey da resposta (se disponível)
        this.capturarDocKeyDaResposta(resposta, '🔑 [MONITOR] docKey capturado ao iniciar');
        
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  // ============================================================================
  // COMANDOS PRINCIPAIS DO MONITOR
  // ============================================================================

  /**
   * 🛑 PARADA COMPLETA PARA SALVAMENTO
   *
   * Para o monitor 100%:
   * 1. Envia comando "parar" ao C#
   * 2. Limpa TODOS os handlers registrados
   * 3. Marca flag _stoppedForSave = true
   *
   * Após chamar este método, NENHUM evento será processado.
   * Usar quando vamos salvar e fechar o arquivo.
   *
   * @param maxRetries Número máximo de tentativas para parar no C#
   * @param timeoutMs Timeout por tentativa
   * @returns MonitorResult com sucesso/falha
   */
  public async stopCompletelyForSave(maxRetries: number = 3, timeoutMs: number = 2000): Promise<MonitorResult> {
    sendToTerminal('🛑 [MONITOR:STOP_COMPLETE] Iniciando parada COMPLETA para salvamento', {
      handlersAntes: this.handlers.size,
      eventosRegistrados: Array.from(this.handlers.keys()),
      timestamp: new Date().toISOString()
    }, LogCategory.EVENT);

    // 1. Marcar flag IMEDIATAMENTE (antes de qualquer await)
    // Isso garante que eventos que chegarem durante a parada serão ignorados
    this._stoppedForSave = true;

    // 2. Limpar TODOS os handlers IMEDIATAMENTE
    const handlersLimpos = this.handlers.size;
    this.handlers.clear();

    sendToTerminal('✅ [MONITOR:STOP_COMPLETE] Handlers limpos', {
      handlersLimpos,
      handlersDepois: this.handlers.size
    }, LogCategory.EVENT);

    // 3. Parar no C# (com retry)
    const result = await this.stopWithRetry(maxRetries, timeoutMs);

    // 4. Limpar estado
    this.currentDocKey = null;
    this.monitoramentoAtivo = false;
    this._monitorStartedAt = 0;

    sendToTerminal(result.success ? '✅ [MONITOR:STOP_COMPLETE] Parada completa concluída' : '⚠️ [MONITOR:STOP_COMPLETE] Parada no C# falhou, mas handlers já foram limpos', {
      success: result.success,
      error: result.error,
      stoppedForSave: this._stoppedForSave
    }, LogCategory.EVENT);

    return result;
  }

  /**
   * Reseta o flag de parada para salvamento
   * Usar apenas se precisar reiniciar o monitor após um erro
   */
  public resetStoppedForSave(): void {
    this._stoppedForSave = false;
    sendToTerminal('🔄 [MONITOR] Flag stoppedForSave resetado', {}, LogCategory.EVENT);
  }

  /**
   * Para o monitoramento com retry e proteção contra concorrência
   * @param maxRetries Número máximo de tentativas
   * @param timeoutMs Timeout por tentativa em milissegundos
   */
  public async stopWithRetry(maxRetries: number = 3, timeoutMs: number = 2000): Promise<MonitorResult> {
    if (this._stopInProgress) {
      return { success: false, error: 'Stop já em progresso' };
    }

    this._stopInProgress = true;
    
    try {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const resultado = await Promise.race([
            this.pararMonitoramento(),
            new Promise<boolean>((_, reject) => 
              setTimeout(() => reject(new Error('Timeout')), timeoutMs)
            )
          ]);

          if (resultado) {
            sendToTerminal('✅ [MONITOR] Monitor parado com sucesso', { attempt });
            return { success: true, message: 'Monitor parado', data: { attempt } };
          }
        } catch (error) {
          if (attempt === maxRetries) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            sendToTerminal('❌ [MONITOR] Falha ao parar monitor após retries', { 
              attempt, 
              maxRetries, 
              error: errorMsg 
            });
            return { success: false, error: errorMsg };
          }
          // Esperar antes de tentar novamente
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      return { success: false, error: 'Máximo de tentativas excedido' };
    } finally {
      this._stopInProgress = false;
    }
  }


  /**
   * Para o monitoramento (método original mantido para compatibilidade)
   */
  public async pararMonitoramento(): Promise<boolean> {
    // 🔍 LOG CRÍTICO: Rastrear origem da chamada
    const stackTrace = new Error().stack?.split('\n').slice(1, 10) || [];
    const callerInfo = stackTrace
      .map(line => line.trim())
      .filter(line => !line.includes('MonitoradorObjetos.ts'))
      .slice(0, 5)
      .join(' → ');
    
    // ✅ LOG DETALHADO: Stack trace completo para debug
    const fullStack = stackTrace.slice(0, 10).map((line, idx) => `${idx + 1}. ${line}`).join('\n');
    
    sendToTerminal('🛑 [MONITOR:PARAR] Comando PARAR sendo executado', {
      monitorAtivo: this.monitoramentoAtivo,
      callerStack: callerInfo,
      callerStackFull: fullStack,
      timestamp: new Date().toISOString()
    }, LogCategory.EVENT);
    
    // ✅ Também enviar para canal csharp com stack trace completo
    sendToTestLog('🛑 [MONITOR:PARAR] Comando PARAR sendo executado', {
      monitorAtivo: this.monitoramentoAtivo,
      callerStack: callerInfo,
      callerStackFull: fullStack,
      stackTraceLines: stackTrace.slice(0, 10),
      timestamp: new Date().toISOString()
    }, 'csharp', LogCategory.EVENT);
    
    try {
      // Enviar action para o backend C# parar o monitoramento
      const resposta = await this.executarComando({
        action: 'monitorar-objetos',
        data: { comando: 'parar' }
      });

      // ✅ IDEMPOTÊNCIA: Se monitor já está parado, considerar sucesso
      const mensagem = (resposta as any)?.result?.mensagem || (resposta as any)?.message || '';
      const jaEstaParado = mensagem.toLowerCase().includes('não está ativo') || 
                           mensagem.toLowerCase().includes('nao esta ativo');
      
      if (jaEstaParado) {
        // Monitor já estava parado - considerar sucesso (idempotência)
        this.monitoramentoAtivo = false;
        this.currentDocKey = null;
        sendToTerminal('✅ [MONITOR:PARAR] Monitor já estava parado (idempotência)', {
          callerStack: callerInfo,
          mensagem
        }, LogCategory.EVENT);
        return true;
      }

      if (MonitorResponseAdapter.isSuccess(resposta)) {
        this.monitoramentoAtivo = false;
        this.currentDocKey = null; // Limpar docKey ao parar
        sendToTerminal('✅ [MONITOR:PARAR] Monitor parado com sucesso', {
          callerStack: callerInfo
        }, LogCategory.EVENT);
        sendToTerminal('🔑 [MONITOR] docKey limpo ao parar monitoramento', {}, LogCategory.EVENT);
        return true;
      }
      
      sendToTerminal('❌ [MONITOR:PARAR] Falha ao parar - resposta não foi sucesso', {
        status: resposta?.status,
        callerStack: callerInfo
      }, LogCategory.ERROR);
      return false;
    } catch (error) {
      sendToTerminal('💥 [MONITOR:PARAR] Exceção ao parar monitoramento', {
        error: error instanceof Error ? error.message : String(error),
        callerStack: callerInfo
      }, LogCategory.ERROR);
      return false;
    }
  }

  /**
   * Verifica o status do monitoramento
   */
  public async verificarStatus(): Promise<MonitorStatusData | null> {
    try {
      if (this._statusInProgress) {
        // ✅ Log removido - reduzir verbosidade
        return null;
      }
      this._statusInProgress = true;
      // Enviar action para o backend C# verificar status
      const resposta = await this.executarComando({
        action: 'monitorar-objetos',
        data: { comando: 'status' }
      });
      
      if (!MonitorResponseAdapter.isSuccess(resposta)) {
        sendToTerminal('❌ [MONITORAR-OBJETOS] Erro ao verificar status', { 
          error: MonitorResponseAdapter.getErrorMessage(resposta)
        });
        return null;
      }

      const data = MonitorResponseAdapter.getData<MonitorStatusData>(resposta, 'status');
      
      this.webViewInicializado = true;
      this.monitoramentoAtivo = data.ativo || false;
      
      return data;
    } catch (error) {
      sendToTerminal('❌ [MONITORAR-OBJETOS] Exceção em verificarStatus', {
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    } finally {
      this._statusInProgress = false;
    }
  }

  // ╔═══════════════════════════════════════════════════════════════════════╗
  // ║              MÉTODOS ESPECÍFICOS POR PIPELINE                         ║
  // ╚═══════════════════════════════════════════════════════════════════════╝

  /**
   * 🛒 SALES PIPELINE: Inicializar monitor para salvamento
   * 
   * ✅ Este método É CHAMADO DIRETAMENTE (sem pipeline prévia)
   * Por isso PRECISA verificar/parar monitor anterior.
   * 
   * Diferença com initForProduction():
   * - Sales: chamado direto → PRECISA verificar/parar monitor ✅
   * - Production: chamado após pipeline → NÃO precisa (pipeline já fez) ❌
   * 
   * Características:
   * - Documento JÁ ESTÁ ABERTO
   * - PIN = false (segue documento ativo)
   * - Polling rápido (200ms)
   * - Eventos em tempo real
   * 
   * @returns MonitorResult com sucesso/falha e dados do sync
   */
  public async initForSales(): Promise<MonitorResult> {
    this._currentContext = 'sales';
    sendToTerminal('🛒 [MONITOR:SALES] Iniciando monitor para Sales pipeline');

    try {
      // ✅ CORRETO: Verificar/parar monitor (Sales é chamado diretamente, sem pipeline prévia)
      // 1. Verificar se já existe monitor ativo
      const status = await this.verificarStatus();
      if (status?.ativo) {
        sendToTerminal('⚠️ [MONITOR:SALES] Monitor já ativo, parando primeiro');
        const stopResult = await this.stopWithRetry(3, 2000);
        if (!stopResult.success) {
          return { success: false, error: 'Falha ao parar monitor anterior' };
        }
        // Aguardar estabilização
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // 2. Configuração otimizada para Sales
      const config: ConfiguracaoMonitoramento = {
        pollingInterval: 200,  // Rápido para UI responsiva
        batchSize: 1,          // Eventos individuais
        snapshotPaginado: false,
        pin: false             // Segue documento ativo
      };

      // 3. Iniciar monitor
      const inicioResult = await this.iniciarComConfiguracao(config);
      if (inicioResult?.status !== 'ok') {
        return { 
          success: false, 
          error: 'Falha ao iniciar monitor',
          data: inicioResult 
        };
      }

      // 4. Aguardar estabilização
      await new Promise(resolve => setTimeout(resolve, 300));

      // 5. Sincronizar objetos
      const syncResult = await this.sincronizarObjetos();
      if (syncResult?.status !== 'ok') {
        return { 
          success: false, 
          error: 'Falha no sync inicial',
          data: syncResult 
        };
      }

      sendToTerminal('✅ [MONITOR:SALES] Monitor inicializado com sucesso', {
        objetosMonitorados: syncResult?.result?.objetosMonitorados,
        objetos: syncResult?.result?.objetos?.length
      });

      return {
        success: true,
        message: 'Monitor Sales inicializado',
        data: syncResult?.result
      };

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      sendToTerminal('❌ [MONITOR:SALES] Erro ao inicializar', { error: errorMsg });
      return { success: false, error: errorMsg };
    }
  }

  /**
   * 🏭 PRODUCTION PIPELINE: Inicializar monitor para produção/exportação
   * 
   * ⚠️ IMPORTANTE: Este método assume que o monitor JÁ FOI PARADO pela pipeline
   * antes de chamar este método. A verificação/parada é feita em useProductionOpenFlow.ts (linhas 419-458)
   * 
   * Características:
   * - Arquivo JÁ FOI ABERTO pela pipeline antes deste método
   * - Pipeline JÁ verificou/parou monitor anterior (se necessário)
   * - PIN = true (fixa no documento aberto)
   * - Polling moderado (200ms)
   * - Validação de convergência
   * - Delays aumentados: 1000ms + 500ms + 500ms = 2000ms total
   * - Suporte a retry com confirmação do usuário
   * 
   * Sequência esperada (gerenciada pela pipeline):
   * 1. Pipeline verifica/para monitor anterior → useProductionOpenFlow.ts linhas 419-458
   * 2. Pipeline abre arquivo → abrirArquivoComCredenciais()
   * 3. Pipeline chama initForProduction() ← VOCÊ ESTÁ AQUI
   * 4. Este método: aguarda estabilização → init → status (1s) → aguarda (0.5s) → sync (após 0.5s) → retorna dados
   * 
   * @param caminhoArquivo Caminho do arquivo que foi aberto (apenas para logs)
   * @param maxRetries Número máximo de tentativas (padrão: 1, sem retry automático)
   * @returns MonitorResult com sucesso/falha e dados do sync
   */
  public async initForProduction(caminhoArquivo?: string, maxRetries: number = 1): Promise<MonitorResult> {
    this._currentContext = 'production';
    
    let lastError: string = '';
    let lastSyncData: any = null;
    
    // Loop de tentativas
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      sendToTerminal(`🏭 [MONITOR:PRODUCTION] Tentativa ${attempt}/${maxRetries} - Iniciando monitor`, {
        caminhoArquivo: caminhoArquivo ? caminhoArquivo.substring(caminhoArquivo.lastIndexOf('\\') + 1) : 'N/A'
      });

      try {
        // 1. Aguardar arquivo estabilizar (apenas na primeira tentativa)
        if (caminhoArquivo && attempt === 1) {
          sendToTerminal('⏳ [MONITOR:PRODUCTION] Aguardando arquivo estabilizar (2000ms)');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        // 2. Configuração otimizada para Production
        const config: ConfiguracaoMonitoramento = {
          pollingInterval: 200,
          batchSize: 1,
          snapshotPaginado: false,
          pin: true  // ⚠️ CRÍTICO: Fixar no documento aberto
        };

        // 3. Iniciar monitor COM PIN
        sendToTerminal('▶️ [MONITOR:PRODUCTION] Iniciando monitor com PIN=true');
        const inicioResult = await this.iniciarComConfiguracao(config);
        
        // Validar resposta do iniciar
        const monitorActive = inicioResult?.result?.active === true || 
                             inicioResult?.status === 'ok';
        
        if (!monitorActive) {
          lastError = 'Monitor não foi iniciado corretamente';
          lastSyncData = inicioResult;
          sendToTerminal(`❌ [MONITOR:PRODUCTION] Tentativa ${attempt} - Monitor não iniciado`, {
            status: inicioResult?.status,
            resultActive: inicioResult?.result?.active
          });
          continue; // Próxima tentativa
        }

        sendToTerminal('✅ [MONITOR:PRODUCTION] Comando iniciar aceito', {
          status: inicioResult?.status,
          active: inicioResult?.result?.active
        });

        // 4. ✅ AGUARDAR MONITOR PROCESSAR (AUMENTADO: 1000ms)
        sendToTerminal(`⏳ [MONITOR:PRODUCTION] Aguardando ${OPERATION_DELAYS.PRODUCTION_MONITOR_INIT}ms para monitor processar`);
        await new Promise(resolve => setTimeout(resolve, OPERATION_DELAYS.PRODUCTION_MONITOR_INIT));

        // 5. Verificar status (informativo)
        const statusCheck = await this.verificarStatus();
        sendToTerminal('📊 [MONITOR:PRODUCTION] Status check', {
          ativo: statusCheck?.ativo,
          objetosMonitorados: statusCheck?.objetosMonitorados,
          documentoAtivo: statusCheck?.documentoAtivo
        });

        // 6. ✅ AGUARDAR APÓS STATUS (NOVO: 500ms)
        sendToTerminal(`⏳ [MONITOR:PRODUCTION] Aguardando ${OPERATION_DELAYS.PRODUCTION_AFTER_STATUS}ms após status`);
        await new Promise(resolve => setTimeout(resolve, OPERATION_DELAYS.PRODUCTION_AFTER_STATUS));

        // 7. ✅ AGUARDAR ANTES DO SYNC (NOVO: 500ms)
        sendToTerminal(`⏳ [MONITOR:PRODUCTION] Aguardando ${OPERATION_DELAYS.PRODUCTION_BEFORE_SYNC}ms antes do sync`);
        await new Promise(resolve => setTimeout(resolve, OPERATION_DELAYS.PRODUCTION_BEFORE_SYNC));

        // 8. Sincronizar objetos (validação real)
        sendToTerminal(`🔄 [MONITOR:PRODUCTION] Tentativa ${attempt} - Sincronizando objetos`);
        const syncResult = await this.sincronizarObjetos();
        
        // Extrair campos conforme documentação monitorar-objetos
        const code = syncResult?.result?.code;
        const sucesso = syncResult?.result?.sucesso === true;
        const objetos = syncResult?.result?.objetos || [];
        const objetosMonitorados = syncResult?.result?.objetosMonitorados || 0;

        // ✅ Log detalhado do resultado do sync
        sendToTerminal('🔍 [MONITOR:PRODUCTION] Resultado do sync', {
          attempt,
          status: syncResult?.status,
          code,
          sucesso,
          objetosLength: objetos.length,
          objetosMonitorados,
          arrayIsValid: Array.isArray(objetos)
        });

        // Verificar se monitor pausou (documento trocado)
        if (code === 'monitor_paused_doc_switched') {
          lastError = 'Monitor pausado: documento foi trocado durante sync';
          lastSyncData = syncResult?.result;
          sendToTerminal(`⚠️ [MONITOR:PRODUCTION] Tentativa ${attempt} - Documento trocado`);
          continue; // Próxima tentativa
        }

        // Validar status geral
        if (syncResult?.status !== 'ok') {
          lastError = 'Falha no sync inicial';
          lastSyncData = syncResult;
          sendToTerminal(`❌ [MONITOR:PRODUCTION] Tentativa ${attempt} - Status não OK`, {
            status: syncResult?.status
          });
          continue; // Próxima tentativa
        }

        // Validar sucesso E objetos
        if (!sucesso || !Array.isArray(objetos) || objetos.length === 0) {
          lastError = 'Sync não retornou objetos válidos';
          lastSyncData = syncResult?.result;
          sendToTerminal(`❌ [MONITOR:PRODUCTION] Tentativa ${attempt} - Sem objetos válidos`, {
            sucesso,
            isArray: Array.isArray(objetos),
            length: objetos.length
          });
          continue; // Próxima tentativa
        }

        // Validação adicional de convergência
        if (objetosMonitorados === 0) {
          lastError = 'Monitor não reportou objetos monitorados';
          lastSyncData = syncResult?.result;
          sendToTerminal(`❌ [MONITOR:PRODUCTION] Tentativa ${attempt} - objetosMonitorados = 0`);
          continue; // Próxima tentativa
        }

        // ✅ SUCESSO!
        sendToTerminal(`✅ [MONITOR:PRODUCTION] Tentativa ${attempt} - Sucesso!`, {
          pin: true,
          objetosMonitorados,
          objetos: objetos.length,
          documentoAtivo: statusCheck?.documentoAtivo,
          totalDelayMs: OPERATION_DELAYS.PRODUCTION_MONITOR_INIT + OPERATION_DELAYS.PRODUCTION_AFTER_STATUS + OPERATION_DELAYS.PRODUCTION_BEFORE_SYNC
        });

        return {
          success: true,
          message: 'Monitor Production inicializado',
          data: {
            ...syncResult?.result,
            documentoAtivo: statusCheck?.documentoAtivo,
            pin: true
          }
        };

      } catch (error) {
        lastError = error instanceof Error ? error.message : String(error);
        sendToTerminal(`❌ [MONITOR:PRODUCTION] Tentativa ${attempt} - Erro`, { error: lastError });
        // Continue para próxima tentativa
      }
    }

    // Se chegou aqui, todas as tentativas falharam
    sendToTerminal('❌ [MONITOR:PRODUCTION] Todas as tentativas falharam', {
      maxRetries,
      lastError,
      lastSyncData
    });

    return { 
      success: false, 
      error: lastError,
      data: lastSyncData 
    };
  }

  /**
   * 📤 EXPORT PIPELINE: Preparar monitor para exportação
   * 
   * Características:
   * - Documento JÁ ESTÁ ABERTO e preparado
   * - Apenas sync de dados
   * - Não precisa iniciar (já gerenciado pelo Sales/Production)
   * 
   * @returns MonitorResult com dados do sync
   */
  public async initForExport(): Promise<MonitorResult> {
    this._currentContext = 'export';
    sendToTerminal('📤 [MONITOR:EXPORT] Preparando para exportação');

    try {
      // 1. Verificar se monitor está ativo
      const status = await this.verificarStatus();
      if (!status?.ativo) {
        return { 
          success: false, 
          error: 'Monitor não está ativo. Inicialize via Sales ou Production primeiro.' 
        };
      }

      // 2. Sincronizar objetos
      const syncResult = await this.sincronizarObjetos();
      if (syncResult?.status !== 'ok') {
        return { 
          success: false, 
          error: 'Falha no sync para exportação',
          data: syncResult 
        };
      }

      sendToTerminal('✅ [MONITOR:EXPORT] Dados sincronizados', {
        objetosMonitorados: syncResult?.result?.objetosMonitorados
      });

      return {
        success: true,
        message: 'Dados sincronizados para exportação',
        data: syncResult?.result
      };

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      sendToTerminal('❌ [MONITOR:EXPORT] Erro ao preparar', { error: errorMsg });
      return { success: false, error: errorMsg };
    }
  }

  /**
   * 🔧 UTILITÁRIO: Garantir que o monitor está pronto para uso
   * 
   * Verifica status e inicia se necessário (modo genérico)
   * 
   * @returns MonitorResult indicando se o monitor está pronto
   */
  public async ensureMonitorReady(): Promise<MonitorResult> {
    try {
      const status = await this.verificarStatus();
      
      if (status?.ativo) {
        return { 
          success: true, 
          message: 'Monitor já ativo',
          data: status 
        };
      }

      // Iniciar em modo genérico
      const resultado = await this.iniciarMonitoramento();
      if (resultado) {
        return { 
          success: true, 
          message: 'Monitor inicializado',
          data: { ativo: true }
        };
      }

      return { success: false, error: 'Falha ao iniciar monitor' };
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMsg };
    }
  }


  // ╔═══════════════════════════════════════════════════════════════════════╗
  // ║                    FIM DOS MÉTODOS POR PIPELINE                       ║
  // ╚═══════════════════════════════════════════════════════════════════════╝

  /**
   * Registra handlers para diferentes tipos de eventos
   */
  public on(tipo: TipoEvento, handler: EventHandler): void {
    if (!this.handlers.has(tipo)) {
      this.handlers.set(tipo, new Set());
    }
    this.handlers.get(tipo)!.add(handler);
  }

  /**
   * Remove um handler específico
   */
  public off(tipo: TipoEvento, handler: EventHandler): void {
    const handlers = this.handlers.get(tipo);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Obtém docKey com fallback quando null
   * 
   * docKey pode ser null quando documento novo não foi salvo ainda.
   * Usa fallback: docKey → documentoAtivo → 'unknown'
   */
  private getDocKeyWithFallback(dados: any, fallback?: string): string {
    const docKey = dados?.docKey;
    if (docKey && typeof docKey === 'string') {
      return docKey;
    }
    
    // Fallback 1: documentoAtivo
    const documentoAtivo = dados?.documentoAtivo || fallback;
    if (documentoAtivo && typeof documentoAtivo === 'string') {
      sendToTerminal('⚠️ [MONITOR] Usando fallback para docKey', {
        docKeyOriginal: docKey,
        fallback: documentoAtivo
      }, LogCategory.EVENT);
      return documentoAtivo;
    }
    
    // Fallback 2: 'unknown'
    sendToTerminal('⚠️ [MONITOR] docKey indisponível, usando "unknown"', {
      docKeyOriginal: docKey
    }, LogCategory.EVENT);
    return 'unknown';
  }

  /**
   * Processa notificações recebidas do CorelDraw
   *
   * Nota: Backend não envia eventos em batch. Snapshot paginado envia múltiplas
   * notificações individuais sequenciais, não uma única notificação batch.
   *
   * Filtra notificações por docKey quando há múltiplos documentos abertos.
   */
  private processarNotificacao(dados: NotificacaoMudanca): void {
    // 🛑 BLOQUEIO TOTAL: Se parado para salvamento, ignorar TODOS os eventos
    if (this._stoppedForSave) {
      // Silencioso - não logar para evitar ruído durante salvamento
      return;
    }

    // ✅ EXCEÇÃO: Eventos de documento "fechado" sempre passam (docKey pode não estar disponível)
    const cat = (dados as any)?.categoria;
    const status = (dados as any)?.status;
    const isDocumentClosedEvent = cat === 'documento' && status === 'fechado';
    
    // Filtrar por docKey se estiver configurado (múltiplos documentos)
    // EXCETO para eventos de "fechado" que sempre devem passar
    if (this.currentDocKey !== null && !isDocumentClosedEvent) {
      const notificationDocKey = this.getDocKeyWithFallback(dados);
      
      // Se docKey da notificação não corresponde ao documento monitorado, ignorar
      if (notificationDocKey !== 'unknown' && notificationDocKey !== this.currentDocKey) {
        // ✅ Log removido - comportamento esperado, reduzir verbosidade (centenas de logs)
        return;
      }
    }
    
    // Processamento individual (comportamento existente)
    // Mapear eventos de documento com campos padronizados (docKey/pinned/status)
    try {
      if (cat === 'documento') {
        const docKey = this.getDocKeyWithFallback(dados);
        
        // Atualizar ou limpar currentDocKey baseado no status do documento
        if (status === 'fechado' || status === 'trocado') {
          // ✅ PROTEÇÃO: Ignorar evento de documento-trocado nos primeiros 5s após iniciar monitor
          // Isso evita race condition onde o CorelDRAW envia evento de documento-trocado
          // logo após abrir um arquivo, o que limparia o docKey prematuramente
          const msSinceStart = this._monitorStartedAt > 0 ? Date.now() - this._monitorStartedAt : Infinity;
          const STABILIZATION_PERIOD_MS = 5000; // 5 segundos de estabilização (aumentado de 3s)
          
          // ✅ VALIDAÇÃO ADICIONAL: Verificar se o evento é do documento correto
          const eventDocKey = docKey !== 'unknown' ? docKey : null;
          const isEventFromCurrentDoc = eventDocKey && this.currentDocKey && eventDocKey === this.currentDocKey;
          const hasNoDocKeyYet = this.currentDocKey === null;
          
          // ✅ IMPORTANTE: Para eventos de "fechado", sempre emitir para o hook
          // O hook fará sua própria validação (docKey match, nome match, etc.)
          // Não retornar aqui - deixar o evento passar para o hook processar
          
          // ✅ IMPORTANTE: NÃO limpar docKey AQUI - emitir evento primeiro
          // O hook precisa do docKey para validar se é o documento correto
          // A limpeza será feita DEPOIS de emitir o evento
          
          if (status === 'fechado') {
            // ✅ Para eventos de "fechado", sempre emitir para hook validar
            sendToTerminal('⏭️ [MONITOR] Evento "fechado" recebido - deixando hook validar', {
              status,
              currentDocKey: this.currentDocKey,
              eventDocKey: eventDocKey,
              isEventFromCurrentDoc,
              msSinceStart,
              reason: hasNoDocKeyYet ? 'sem_dockey_ainda' : !isEventFromCurrentDoc ? 'documento_diferente' : 'periodo_estabilizacao'
            }, LogCategory.EVENT);
            // ✅ NÃO retornar - deixar evento passar para hook
          } else {
            // Para "trocado", aplicar mesma lógica de proteção
            if (msSinceStart < STABILIZATION_PERIOD_MS && (hasNoDocKeyYet || !isEventFromCurrentDoc)) {
              sendToTerminal('⏭️ [MONITOR] Ignorando evento documento-trocado (período de estabilização ou documento diferente)', {
                status,
                msSinceStart,
                stabilizationMs: STABILIZATION_PERIOD_MS,
                currentDocKey: this.currentDocKey,
                eventDocKey: eventDocKey,
                isEventFromCurrentDoc,
                hasNoDocKeyYet
              }, LogCategory.EVENT);
              return; // ✅ Retornar apenas para "trocado" durante período de estabilização
            }
          }
        } else if (status === 'aberto' && docKey !== 'unknown') {
          // Documento aberto - atualizar docKey se ainda não estiver definido
          if (this.currentDocKey === null) {
            this.currentDocKey = docKey;
            // ✅ Log removido - reduzir verbosidade
          }
        }
        
        const mapped = {
          tipo: 'documento',
          status: status,
          docKey: docKey,
          pinned: (dados as any)?.pinned,
          arquivoAtual: (dados as any)?.arquivoAtual,
          arquivoAnterior: (dados as any)?.arquivoAnterior,
          // ✅ IMPORTANTE: Incluir campos adicionais do backend
          monitoramentoParado: (dados as any)?.monitoramentoParado,
          timestamp: (dados as any)?.timestamp
        } as any;
        
        // ✅ IMPORTANTE: Emitir evento ANTES de limpar docKey (para hook validar)
        // O hook precisa do docKey para validar se é o documento correto
        this.processarObjetoIndividual(mapped as any);
        
        // ✅ IMPORTANTE: Limpar docKey APÓS emitir evento (mas apenas se passou período de estabilização)
        // Isso garante que o hook receba o evento com o docKey ainda disponível
        if (status === 'fechado' || status === 'trocado') {
          const msSinceStart = this._monitorStartedAt > 0 ? Date.now() - this._monitorStartedAt : Infinity;
          const STABILIZATION_PERIOD_MS = 5000; // 5 segundos de estabilização
          const eventDocKey = docKey !== 'unknown' ? docKey : null;
          const isEventFromCurrentDoc = eventDocKey && this.currentDocKey && eventDocKey === this.currentDocKey;
          
          // Limpar apenas se passou período de estabilização E evento é do documento correto
          if (this.currentDocKey !== null && isEventFromCurrentDoc && msSinceStart >= STABILIZATION_PERIOD_MS) {
            sendToTerminal('🔑 [MONITOR] docKey limpo - documento fechado/trocado (validado)', {
              status,
              previousDocKey: this.currentDocKey,
              eventDocKey: eventDocKey,
              msSinceStart
            }, LogCategory.EVENT);
            this.currentDocKey = null;
          }
        }
        
        return;
      }
    } catch (err) {
      sendToTerminal('⚠️ [MONITORADOR] ERRO_PROCESSAR_DOCUMENTO', {
        error: err instanceof Error ? err.message : String(err)
      });
    }
    this.processarObjetoIndividual(dados);
  }

  /**
   * Processa um objeto individual (método extraído para reutilização)
   */
  private processarObjetoIndividual(dados: NotificacaoMudanca): void {
    // Normalizar ObjetoCache (PascalCase → camelCase) para compatibilidade com hooks existentes
    const normalizarCache = (obj: any) => {
      if (!obj || typeof obj !== 'object') return obj;
      if ('Nome' in obj) {
        return {
          nome: obj.Nome,
          largura: obj.Largura,
          altura: obj.Altura,
          posicaoX: obj.PosicaoX,
          posicaoY: obj.PosicaoY,
          camada: obj.Camada,
          tipo: obj.Tipo,
          existe: obj.Existe,
          ordem: obj.Ordem,
          staticId: obj.StaticID,
          pageIndex: obj.Pagina,
          bbox: (typeof obj.Left === 'number' && typeof obj.Right === 'number' && typeof obj.Top === 'number' && typeof obj.Bottom === 'number')
            ? { left: obj.Left, right: obj.Right, top: obj.Top, bottom: obj.Bottom }
            : undefined
        };
      }
      return obj;
    };

    try {
      (dados as any).dadosAnteriores = normalizarCache((dados as any).dadosAnteriores);
      (dados as any).dadosAtuais = normalizarCache((dados as any).dadosAtuais);
    } catch {}

    // Aliases/normalização de tipo de evento vindos do backend
    try {
      const tipoOriginal = (dados as any)?.tipo;
      if (tipoOriginal === 'novo') {
        (dados as any).tipo = 'criado';
      }
    } catch {}
    
    // Executar handlers específicos para o tipo de evento
    const handlers = this.handlers.get(dados.tipo);
    if (handlers && handlers.size > 0) {
      handlers.forEach(handler => {
        try {
          handler(dados);
        } catch (error) {
          logger.error('❌ Erro no handler específico:', error);
        }
      });
    } else if (dados.tipo === 'documento') {
      // ✅ LOG CRÍTICO: Nenhum handler registrado para "documento"
      // Isso indica que o handler não foi registrado ainda ou foi desregistrado
      sendToTerminal('⚠️ [MONITOR] Nenhum handler registrado para evento "documento"', {
        tipo: dados.tipo,
        status: (dados as any)?.status,
        docKey: (dados as any)?.docKey,
        handlersDisponiveis: Array.from(this.handlers.keys()),
        totalHandlers: Array.from(this.handlers.values()).reduce((sum, set) => sum + set.size, 0)
      }, LogCategory.EVENT);
    }

    // Executar handlers genéricos (*)
    const handlersGenericos = this.handlers.get('*');
    if (handlersGenericos && handlersGenericos.size > 0) {
      handlersGenericos.forEach(handler => {
        try {
          handler(dados);
        } catch (error) {
          logger.error('❌ Erro no handler genérico:', error);
        }
      });
    }
  }

  /**
   * Executa um comando no backend C#
   */
  public async executarComando(
    comando: any,
    options?: { onProgress?: (response: any) => void; onRequestId?: (requestId: string) => void }
  ): Promise<any> {
    // 🔍 LOG CRÍTICO: Rastrear comando "parar" sendo enviado
    if (comando?.data?.comando === 'parar') {
      const stackTrace = new Error().stack?.split('\n').slice(1, 10) || [];
      const callerInfo = stackTrace
        .map(line => line.trim())
        .filter(line => !line.includes('MonitoradorObjetos.ts') && !line.includes('executarComando'))
        .slice(0, 5)
        .join(' → ');
      
      sendToTerminal('📤 [MONITOR:COMANDO] Enviando comando PARAR ao backend', {
        action: comando.action,
        comando: comando.data?.comando,
        monitorAtivo: this.monitoramentoAtivo,
        callerStack: callerInfo,
        timestamp: new Date().toISOString()
      }, LogCategory.EVENT);
    }
    
    // Reduzir ruído: logs genéricos desativados; manter apenas eventos do monitor
    return new Promise((resolve) => {
      try {
        const requestId = corelCommunication.sendActionGuarded(
          comando.action,
          comando.data,
          (response) => {
            // ✅ ACK de progresso para monitorar-objetos usa type: "progress"
            // Backend C# envia: { type: "progress", message: "monitorando" | "coletando", status: "ok" }
            // Diferente de progress_update (status: "progress_update") usado por outras actions (ex: corel-export-batch)
            // 
            // Comandos que enviam ACK:
            // - iniciar: message = "monitorando"
            // - sync: message = "coletando"
            if (MonitorResponseAdapter.isProgressAck(response)) {
              // É ACK de progresso, repassar para callback e manter handler ativo
              if (options?.onProgress) {
                try {
                  options.onProgress(response);
                } catch (callbackError) {
                  // silencioso
                }
              }
              return; // manter handler até resposta final
            }
            
            // Também manter compatibilidade com formato antigo (progress_update)
            if ((response as any)?.status === 'progress_update') {
              if (options?.onProgress) {
                try {
                  options.onProgress(response);
                } catch (callbackError) {
                  // silencioso
                }
              }
              return;
            }
            
            // Resposta final, resolver Promise
            resolve(response);
          }
        );
        if (requestId && options?.onRequestId) {
          try { options.onRequestId(requestId); } catch {}
        }

        if (!requestId) {
          resolve({ status: 'error', message: 'Falha ao enviar comando' });
        }
      } catch (error) {
        // silencioso
        resolve({ status: 'error', message: 'Erro ao executar comando' });
      }
    });
  }


  /**
   * Solicita snapshot do documento (wrapper para sincronizarObjetos)
   * 
   * Nota: Este método é um wrapper que chama sincronizarObjetos() mas retorna null.
   * Os consumidores usam apenas o side effect (sincronização), não os dados retornados.
   */
  public async snapshot(): Promise<{ snapshotId?: string; totalPaginas?: number; totalObjetos?: number } | null> {
    // Usar sincronizarObjetos() que já gerencia mutex e faz o sync completo
    await this.sincronizarObjetos();
    // Retornar null conforme esperado pelos consumidores (apenas side effect)
    return null;
  }

  /**
   * Sincroniza todos os objetos do documento atual
   * Novo comando para obter estado completo
   * 
   * @param onProgress - Callback opcional para receber ACKs de progresso
   */
  public async sincronizarObjetos(
    onProgress?: (message: string) => void
  ): Promise<MonitorSyncData | null> {
    // #region agent log
    const callStack = new Error().stack?.split('\n').slice(1, 10).map(l => l.trim()).join(' → ') || 'unknown';
    const debugSyncEntry = {
      syncInProgress: this._syncInProgress,
      monitoramentoAtivo: this.monitoramentoAtivo,
      hasOnProgress: !!onProgress,
      callStack: callStack.substring(0, 200)
    };
    if (DEBUG_INGEST_URL) {
      fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoradorObjetos.ts:1160',message:'sincronizarObjetos ENTRY',data:debugSyncEntry,timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
    }
    // #endregion
    
    if (this._syncInProgress) {
      sendToTerminal('⏭️ [MONITOR] Sync ignorado - operação em andamento', {}, LogCategory.EVENT);
      return null;
    }
    this._syncInProgress = true;
    
    try {
      const resposta = await this.executarComando(
        { action: 'monitorar-objetos', data: { comando: 'sync' } },
        {
          onProgress: (ack) => {
            if (MonitorResponseAdapter.isProgressAck(ack)) {
              onProgress?.('Coletando objetos do documento...');
            }
          }
        }
      );
      
      if (!MonitorResponseAdapter.isSuccess(resposta)) {
        // 🔍 DEBUG: Log inline com todos os detalhes para diagnóstico
        const errorMsg = MonitorResponseAdapter.getErrorMessage(resposta);
        const status = resposta?.status ?? 'null';
        const resultSucesso = resposta?.result?.sucesso ?? 'undefined';
        const resultMensagem = resposta?.result?.mensagem ?? 'sem mensagem';
        const responseMessage = (resposta as any)?.message ?? 'sem message';
        
        // Log com dados INLINE na mensagem para garantir exibição
        sendToTerminal(
          `❌ [SYNC_FAIL] status=${status} | sucesso=${resultSucesso} | msg=${resultMensagem} | error=${errorMsg} | response.message=${responseMessage}`,
          { fullResponse: JSON.stringify(resposta).substring(0, 300) },
          LogCategory.ERROR
        );
        return null;
      }
      
      return MonitorResponseAdapter.getData<MonitorSyncData>(resposta, 'sync');
    } catch (error) {
      // #region agent log
      const debugError = {
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        errorName: error instanceof Error ? error.name : typeof error,
        syncInProgress: this._syncInProgress,
        monitoramentoAtivo: this.monitoramentoAtivo
      };
      if (DEBUG_INGEST_URL) {
        fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoradorObjetos.ts:1199',message:'CATCH ERROR in sincronizarObjetos',data:debugError,timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      }
      // #endregion
      // ✅ Log silencioso - erro ignorado conforme solicitado
      // Erro comum quando monitor não está ativo ou resposta inválida
      return null;
    } finally {
      this._syncInProgress = false;
    }
  }

  /**
   * Inicia monitoramento com configurações adaptativas
   * Baseado no tamanho estimado do documento
   */
  public async iniciarComConfiguracao(configuracao: ConfiguracaoMonitoramento): Promise<any> {
    const payload = {
      action: 'monitorar-objetos',
      data: {
        comando: 'iniciar',
        ...configuracao
      }
    };
    const resposta = await this.executarComando(payload);
    
    // Capturar docKey da resposta (se disponível)
    this.capturarDocKeyDaResposta(resposta, '🔑 [MONITOR] docKey capturado ao iniciar com configuração');
    
    // Retornar resposta completa (sucesso ou erro) para compatibilidade
    return resposta;
  }

  /**
   * Configurações otimizadas por cenário
   */
  public static getConfiguracaoPorCenario(tamanhoDocumento: 'pequeno' | 'medio' | 'grande'): ConfiguracaoMonitoramento {
    switch (tamanhoDocumento) {
      case 'pequeno': // < 100 objetos - máxima responsividade
        return {
          pollingInterval: 200,
          batchSize: 1,
          snapshotPaginado: false
        };
      case 'medio': // 100-500 objetos - balanceado
        return {
          pollingInterval: 300,
          batchSize: 5,
          snapshotPaginado: true,
          snapshotPageSize: 30
        };
      case 'grande': // 500+ objetos - foco em performance
        return {
          pollingInterval: 400,
          batchSize: 15,
          snapshotPaginado: true,
          snapshotPageSize: 25
        };
      default:
        return {
          pollingInterval: 300,
          batchSize: 5,
          snapshotPaginado: true,
          snapshotPageSize: 30
        };
    }
  }


  /**
   * Retorna estatísticas dos handlers registrados
   */
  public getEstatisticas(): Estatisticas {
    const alterado = this.handlers.get('alterado')?.size || 0;
    const removido = this.handlers.get('removido')?.size || 0;
    const criado = this.handlers.get('criado')?.size || 0;
    const total = alterado + removido + criado;

    return {
      handlers: {
        alterado,
        removido,
        criado,
        total
      }
    };
  }

  /**
   * Obtém o contexto atual de execução do monitor
   */
  public getCurrentContext(): MonitorContext {
    return this._currentContext;
  }

  /**
   * Verifica se o monitoramento está ativo
   */
  public isMonitorando(): boolean {
    return this.monitoramentoAtivo;
  }

  /**
   * Verifica se o WebView2 está inicializado
   */
  public isWebViewInicializado(): boolean {
    return this.webViewInicializado;
  }

  /**
   * Limpa todos os handlers registrados
   */
  public limparHandlers(): void {
    this.handlers.clear();
  }

  /**
   * Testa a comunicação com o backend
   */
  public async testarComunicacao(): Promise<boolean> {
    try {
      const resposta = await this.executarComando({
        action: 'teste-comunicacao',
        data: {}
      });

      return resposta.status === 'ok';
    } catch (error) {
      return false;
    }
  }

  /**
   * Notifica o backend sobre a disponibilidade do WebView2
   * 
   * ✅ ATUALIZADO: Usa SystemService centralizado
   */
  public async notificarWebViewDisponivel(): Promise<boolean> {
    try {
      // ✅ Usar serviço centralizado
      SystemService.notifyWebViewAvailable({});
      return true;
    } catch (error) {
      sendToTerminal('❌ [MONITOR] Erro ao notificar WebView disponível', {
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }

  /**
   * Coleta metadados completos de objetos específicos
   * @param staticIds Lista de StaticIDs dos objetos para coletar metadados
   * @returns Resposta com metadados completos dos objetos
   */
  public async coletarMetadados(staticIds: string[]): Promise<any> {
    try {
      // Usar MetadataService centralizado
      const result = await MetadataService.collect('document', staticIds);
      
      if (result.sucesso) {
        return { status: 'ok', result };
      } else {
        return { status: 'error', message: result.erro || 'Erro ao coletar metadados' };
      }
    } catch (error) {
      logger.error('❌ Erro ao coletar metadados:', error);
      return { status: 'error', message: error instanceof Error ? error.message : 'Erro desconhecido' };
    }
  }
}

// Instância única do monitorador
export const monitoradorObjetos = new MonitoradorObjetos();
